import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import type { Moment } from 'moment'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import { ItemMetadata } from 'tripPlanner/models'
import LEBookingTag from 'tripPlanner/Tripkit/Common/LEBookingTag'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { typeConstToTitle, formatShortDateRange } from 'tripPlanner/utils'
import { Content } from '.'

const TypeLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
`

const SubContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${rem(4)};
`

interface Props {
  name: string
  type: TripItem['type']
  typeLabel?: string
  isBooked: boolean
  linksToOffer?: boolean
  region?: string
  duration?: string
  onClick?: () => void
  canEdit: boolean
  to?: string
  startDate?: Moment
  endDate?: Moment
  startDay?: number
  endDay?: number
}

export default function DatesVariant({
  name,
  isBooked,
  region,
  startDate,
  endDate,
  duration,
  type,
  typeLabel,
  onClick,
  to,
  linksToOffer = false,
  canEdit,
  startDay,
  endDay,
}: Props) {
  const intl = useIntl()
  const Icon = ItemMetadata[type].icon

  const dateRange = formatShortDateRange({ startDate, endDate, startDay, endDay })
  const datesText = duration
    ? intl.formatMessage(
        {
          id: 'hvLa3j-XQTLMVXj-EptV',
          defaultMessage: '{dateRange} \u2022 {duration}',
          description: 'Trip dates with bullet separator and duration in the trip planner map view',
        },
        { dateRange, duration },
      )
    : dateRange

  const viewOfferLabel = intl.formatMessage({
    id: 'mjNLpunc5331Sv56BeKZ',
    defaultMessage: 'View Offer',
    description: 'Button to view the full offer details on a trip map summary card',
  })
  const viewDetailsLabel = intl.formatMessage({
    id: 'e0RnYa26RXJNDesn6JSR',
    defaultMessage: 'View Details',
    description: 'Button to view item details on a trip map summary card',
  })

  return (
    <Content>
      <Group direction="horizontal" horizontalAlign="space-between">
        <TypeLabel>
          <Icon />
          <BodyText variant="small">
            {typeLabel ?? typeConstToTitle(type)}
          </BodyText>
        </TypeLabel>
        {isBooked && <BookedLabel />}
      </Group>
      <SubContent>
        {region && <BodyText variant="small">{region}</BodyText>}
        <Heading variant="heading6" lineClamp={3}>
          {name}
        </Heading>
        <BodyText variant="small">
          {datesText}
        </BodyText>
      </SubContent>
      {isBooked && linksToOffer && <LEBookingTag />}
      <TextButton
        kind="primary"
        to={to}
        onClick={onClick}
        target={!canEdit ? '_blank' : undefined}
      >
        {linksToOffer ? viewOfferLabel : viewDetailsLabel}
      </TextButton>
    </Content>
  )
}
